/**
 * HRPortal.jsx
 * Standalone HR Resourcing Portal with Role-Based Access Control
 *
 * Roles:
 *   admin  — Full access: all candidates, proposals, client token management
 *   client — Read-only: only candidates/proposals assigned to their company, CV download
 *
 * Authentication:
 *   Admin  → username + password (HR Admin)
 *   Client → company access token (issued via Admin → Client Access tab)
 */
import React, { useState, useEffect } from 'react';
import { Users, Shield, Key, LogOut, Eye, EyeOff, AlertCircle, Lock } from 'lucide-react';
import { fetchClientTokens, appendActivityLog } from '../utils/hrGithubStorage';
import HRResourcing from './HRResourcing';

// ── Credentials ───────────────────────────────────────────────────────────────
// HR Admin credentials (separate from main site admin)
const HR_ADMIN_USER = 'hradmin';
const HR_ADMIN_PASS = 'nibyor@HR2025';

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onAdminLogin, onClientLogin, loading, error }) {
  const [mode,     setMode]     = useState(''); // '' | 'admin' | 'client'
  const [user,     setUser]     = useState('');
  const [pass,     setPass]     = useState('');
  const [token,    setToken]    = useState('');
  const [showPass, setShowPass] = useState(false);

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    onAdminLogin(user.trim(), pass);
  };

  const handleClientSubmit = (e) => {
    e.preventDefault();
    onClientLogin(token.trim());
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex justify-center px-4 pt-8 pb-8 min-h-[calc(100vh-64px)]">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-2xl backdrop-blur-sm mb-3">
            <Users className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">HR Resourcing Portal</h1>
          <p className="text-blue-200 mt-0.5 text-sm">Nibyor Tech — Talent Management</p>
        </div>

        {mode === '' && (
          <div className="space-y-3">
            <button
              onClick={() => setMode('admin')}
              className="w-full flex items-center gap-4 p-5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-2xl border border-white/20 text-white transition-all group"
            >
              <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center group-hover:bg-blue-500/50 transition-colors">
                <Shield className="w-6 h-6 text-blue-200" />
              </div>
              <div className="text-left">
                <p className="font-bold text-base">HR Admin Login</p>
                <p className="text-blue-200 text-sm">Full access — manage candidates, proposals, clients</p>
              </div>
            </button>

            <button
              onClick={() => setMode('client')}
              className="w-full flex items-center gap-4 p-5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-2xl border border-white/20 text-white transition-all group"
            >
              <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center group-hover:bg-purple-500/50 transition-colors">
                <Key className="w-6 h-6 text-purple-200" />
              </div>
              <div className="text-left">
                <p className="font-bold text-base">Client Access</p>
                <p className="text-purple-200 text-sm">View candidates proposed to your company</p>
              </div>
            </button>
          </div>
        )}

        {/* Admin login form */}
        {mode === 'admin' && (
          <form onSubmit={handleAdminSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-white font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-blue-300" /> HR Admin Login</h2>
              <button type="button" onClick={() => setMode('')} className="text-white/50 hover:text-white text-sm">← Back</button>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}
            <div>
              <label className="block text-blue-200 text-xs mb-1 font-medium uppercase tracking-wide">Username</label>
              <input
                autoFocus
                type="text"
                value={user}
                onChange={e => setUser(e.target.value)}
                placeholder="Enter username"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-blue-200 text-xs mb-1 font-medium uppercase tracking-wide">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={pass}
                  onChange={e => setPass(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-colors pr-12"
                  required
                />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">
                  {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in as Admin'}
            </button>
          </form>
        )}

        {/* Client access form */}
        {mode === 'client' && (
          <form onSubmit={handleClientSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-white font-bold flex items-center gap-2"><Key className="w-5 h-5 text-purple-300" /> Client Access</h2>
              <button type="button" onClick={() => setMode('')} className="text-white/50 hover:text-white text-sm">← Back</button>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}
            <div>
              <label className="block text-purple-200 text-xs mb-1 font-medium uppercase tracking-wide">Access Token</label>
              <input
                autoFocus
                type="text"
                value={token}
                onChange={e => setToken(e.target.value)}
                placeholder="Paste your company access token"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:bg-white/15 transition-colors font-mono text-sm"
                required
              />
              <p className="text-white/40 text-xs mt-2">Your access token was provided by Nibyor Tech. Contact us if you haven't received one.</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {loading ? 'Validating…' : 'Access Portal'}
            </button>
          </form>
        )}

        <p className="text-center text-white/30 text-xs mt-6">
          Powered by Nibyor Tech &mdash; Secure Talent Management Platform
        </p>
      </div>
    </div>
  );
}

// ── Main HRPortal ─────────────────────────────────────────────────────────────
const HRPortal = () => {
  const [role,           setRole]           = useState(null);   // null | 'admin' | 'client'
  const [clientCompany,  setClientCompany]  = useState(null);   // { companyName, token, ... }
  const [loading,        setLoading]        = useState(false);
  const [authError,      setAuthError]      = useState('');
  const [clientTokens,   setClientTokens]   = useState(null);   // null = not yet loaded

  // No pre-load: always fetch fresh on login so newly-created tokens are recognised

  const handleAdminLogin = (username, password) => {
    setAuthError('');
    if (username === HR_ADMIN_USER && password === HR_ADMIN_PASS) {
      setRole('admin');
    } else {
      setAuthError('Invalid username or password.');
    }
  };

  const handleClientLogin = async (token) => {
    setAuthError('');
    setLoading(true);
    try {
      // Always fetch fresh from GitHub so tokens saved by admin are visible immediately
      const result = await fetchClientTokens();
      const tokens = result.tokens || [];
      setClientTokens(tokens);
      const company = tokens.find(t => t.token === token.trim() && t.active !== false);
      if (company) {
        setClientCompany(company);
        setRole('client');
        // Log login event asynchronously — does not block UI
        appendActivityLog({
          type:        'login',
          company:     company.companyName,
          token:       token.trim(),
          userAgent:   navigator.userAgent
        });
      } else {
        setAuthError('Invalid or inactive access token. Please contact Nibyor Tech.');
      }
    } catch (e) {
      setAuthError('Could not validate token. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setRole(null);
    setClientCompany(null);
    setAuthError('');
    setClientTokens(null); // reset cache so next login always re-fetches
  };

  // ── Not authenticated ────────────────────────────────────────────────────
  if (!role) {
    return (
      <LoginScreen
        onAdminLogin={handleAdminLogin}
        onClientLogin={handleClientLogin}
        loading={loading}
        error={authError}
      />
    );
  }

  // ── Authenticated — render tracker with role context ─────────────────────
  return (
    <div>
      {/* Top bar with role indicator + logout */}
      <div className={`flex items-center justify-between px-4 py-2 text-sm font-medium text-white ${role === 'admin' ? 'bg-blue-700' : 'bg-purple-700'}`}>
        <div className="flex items-center gap-2">
          {role === 'admin' ? (
            <><Shield className="w-4 h-4" /> HR Admin — Full Access</>
          ) : (
            <><Lock className="w-4 h-4" /> Client View — {clientCompany?.companyName}</>
          )}
        </div>
        <button onClick={handleLogout} className="flex items-center gap-1 hover:opacity-80 transition-opacity">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>

      {/* Tracker — passes down role and company */}
      <div className="p-4">
        <HRResourcing isAdmin={role === 'admin'} clientCompany={role === 'client' ? clientCompany : null} />
      </div>
    </div>
  );
};

export default HRPortal;
